import { getBackendUrl } from '../config/api';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { PieChart, Pie, Cell, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Plus, Download, Calendar, IndianRupee, TrendingUp, Filter, Search, Edit, Trash2, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';

const API = `${getBackendUrl()}/api`;

const ExpenseManagement = () => {
  // State management
  const [expenses, setExpenses] = useState([]);
  const [expenseTypes, setExpenseTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [dateRange, setDateRange] = useState('this_month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filterExpenseType, setFilterExpenseType] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterPaymentMethod, setFilterPaymentMethod] = useState('');
  const [filterRecurringType, setFilterRecurringType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modals
  const [showAddExpenseModal, setShowAddExpenseModal] = useState(false);
  const [showAddTypeModal, setShowAddTypeModal] = useState(false);
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
  const [showAddSubcategoryModal, setShowAddSubcategoryModal] = useState(false);
  const [showEditSubcategoryModal, setShowEditSubcategoryModal] = useState(false);
  const [showEditCategoryModal, setShowEditCategoryModal] = useState(false);
  const [showManageCategoriesModal, setShowManageCategoriesModal] = useState(false);
  const [showGenerateDueModal, setShowGenerateDueModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  
  // Form data
  const [expenseForm, setExpenseForm] = useState({
    expense_type_id: '',
    category_id: '',
    subcategory_id: '',
    amount: '',
    payment_method: 'Savings',
    transaction_number: '',
    date: new Date().toISOString().split('T')[0],
    notes: '',
    status: 'pending',
    recurring_type: null
  });
  
  const [newTypeName, setNewTypeName] = useState('');
  const [newCategoryForm, setNewCategoryForm] = useState({
    name: '',
    subcategories: []
  });
  const [newSubcategoryForm, setNewSubcategoryForm] = useState({
    category_id: '',
    name: '',
    recurring_type: 'one_time'
  });
  
  const [generateDueData, setGenerateDueData] = useState(null);
  const [selectedDueExpenses, setSelectedDueExpenses] = useState([]);
  const [generateDueType, setGenerateDueType] = useState('month'); // 'month', 'quarter', 'year'
  const [generateDueMonth, setGenerateDueMonth] = useState('');
  const [generateDueQuarter, setGenerateDueQuarter] = useState('');
  const [generateDueYear, setGenerateDueYear] = useState('');

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];
  const paymentMethods = ['Savings', 'Current', 'Credit Card', 'Cash'];

  useEffect(() => {
    fetchData();
  }, [dateRange, startDate, endDate, filterExpenseType, filterCategory, filterPaymentMethod, filterRecurringType, filterStatus, searchQuery]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Calculate date range
      const { start, end } = getDateRange();

      // Fetch all data in parallel
      const [expensesRes, expenseTypesRes, categoriesRes, statsRes] = await Promise.all([
        axios.get(`${API}/expenses`, { 
          headers, 
          params: { 
            start_date: start, 
            end_date: end,
            expense_type_id: filterExpenseType || undefined,
            category_id: filterCategory || undefined,
            payment_method: filterPaymentMethod || undefined,
            recurring_type: filterRecurringType || undefined,
            status: filterStatus || undefined,
            search: searchQuery || undefined
          } 
        }),
        axios.get(`${API}/expenses/types`, { headers }),
        axios.get(`${API}/expenses/categories`, { headers }),
        axios.get(`${API}/expenses/dashboard/stats`, { headers, params: { start_date: start, end_date: end } })
      ]);

      // Remove duplicates from categories based on ID
      const uniqueCategories = Array.from(
        new Map(categoriesRes.data.map(cat => [cat.id, cat])).values()
      );
      
      // Also deduplicate subcategories within each category
      const deduplicatedCategories = uniqueCategories.map(cat => ({
        ...cat,
        subcategories: Array.from(
          new Map(cat.subcategories.map(sub => [sub.id, sub])).values()
        )
      }));

      setExpenses(expensesRes.data);
      setExpenseTypes(expenseTypesRes.data);
      setCategories(deduplicatedCategories);
      setDashboardStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDateRange = () => {
    const today = new Date();
    let start, end;

    switch (dateRange) {
      case 'today':
        start = end = today.toISOString().split('T')[0];
        break;
      case 'this_week':
        const startOfWeek = new Date(today.setDate(today.getDate() - today.getDay()));
        start = startOfWeek.toISOString().split('T')[0];
        end = new Date().toISOString().split('T')[0];
        break;
      case 'this_month':
        start = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
        end = new Date().toISOString().split('T')[0];
        break;
      case 'custom':
        start = startDate;
        end = endDate;
        break;
      default:
        start = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
        end = new Date().toISOString().split('T')[0];
    }

    return { start, end };
  };

  const handleAddExpense = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      if (editingExpense) {
        // Merge with existing expense data to preserve all fields
        const mergedData = {
          ...editingExpense,  // Start with all existing expense data
          ...expenseForm,     // Override with form data
          id: editingExpense.id // Preserve ID
        };
        
        console.log('[UPDATE EXPENSE] Merged data being sent:', mergedData);
        
        await axios.put(`${API}/expenses/${editingExpense.id}`, mergedData, { headers });
      } else {
        await axios.post(`${API}/expenses`, expenseForm, { headers });
      }

      setShowAddExpenseModal(false);
      setEditingExpense(null);
      resetExpenseForm();
      fetchData();
    } catch (error) {
      console.error('Error saving expense:', error);
      alert('Failed to save expense');
    }
  };

  const handleDeleteExpense = async (id) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/expenses/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      console.error('Error deleting expense:', error);
      alert('Failed to delete expense');
    }
  };

  const handleAddExpenseType = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/expenses/types`, { name: newTypeName }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowAddTypeModal(false);
      setNewTypeName('');
      fetchData();
    } catch (error) {
      console.error('Error adding expense type:', error);
      alert('Failed to add expense type');
    }
  };

  const handleAddCategory = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/expenses/categories`, newCategoryForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowAddCategoryModal(false);
      setNewCategoryForm({ name: '', subcategories: [] });
      fetchData();
    } catch (error) {
      console.error('Error adding category:', error);
      alert('Failed to add category');
    }
  };

  const handleAddSubcategory = async () => {
    try {
      if (!newSubcategoryForm.category_id) {
        alert('Please select a category first');
        return;
      }
      
      if (!newSubcategoryForm.name.trim()) {
        alert('Subcategory name is required');
        return;
      }
      
      const token = localStorage.getItem('token');
      console.log('Adding subcategory to category:', newSubcategoryForm.category_id);
      console.log('Subcategory data:', { name: newSubcategoryForm.name, recurring_type: newSubcategoryForm.recurring_type });
      
      const response = await axios.post(
        `${API}/expenses/categories/${newSubcategoryForm.category_id}/subcategories`, 
        { name: newSubcategoryForm.name, recurring_type: newSubcategoryForm.recurring_type },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('Subcategory added successfully:', response.data);
      alert('Subcategory added successfully!');
      setShowAddSubcategoryModal(false);
      setNewSubcategoryForm({ category_id: '', name: '', recurring_type: 'one_time' });
      fetchData();
    } catch (error) {
      console.error('Error adding subcategory:', error);
      console.error('Error response:', error.response?.data);
      alert('Failed to add subcategory: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEditSubcategory = async () => {
    try {
      if (!editingSubcategory) return;
      
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/expenses/categories/${editingSubcategory.category_id}/subcategories/${editingSubcategory.id}`,
        { name: editingSubcategory.name, recurring_type: editingSubcategory.recurring_type },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert('Subcategory updated successfully!');
      setShowEditSubcategoryModal(false);
      setEditingSubcategory(null);
      fetchData();
    } catch (error) {
      console.error('Error updating subcategory:', error);
      alert('Failed to update subcategory: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteSubcategory = async (categoryId, subcategoryId, subcategoryName) => {
    if (!window.confirm(`Are you sure you want to delete "${subcategoryName}"?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API}/expenses/categories/${categoryId}/subcategories/${subcategoryId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert('Subcategory deleted successfully!');
      fetchData();
    } catch (error) {
      console.error('Error deleting subcategory:', error);
      alert('Failed to delete subcategory: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEditCategory = async () => {
    try {
      if (!editingCategory) return;
      
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/expenses/categories/${editingCategory.id}`,
        { name: editingCategory.name },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert('Category updated successfully!');
      setShowEditCategoryModal(false);
      setEditingCategory(null);
      fetchData();
    } catch (error) {
      console.error('Error updating category:', error);
      alert('Failed to update category: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteCategory = async (categoryId, categoryName) => {
    if (!window.confirm(`Are you sure you want to delete category "${categoryName}"? This will also delete all its subcategories.`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API}/expenses/categories/${categoryId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert('Category deleted successfully!');
      fetchData();
    } catch (error) {
      console.error('Error deleting category:', error);
      alert('Failed to delete category: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleGenerateDue = async () => {
    try {
      const token = localStorage.getItem('token');
      let targetMonth;
      
      // Calculate target month based on selection type
      if (generateDueType === 'month' && generateDueMonth) {
        targetMonth = generateDueMonth;
      } else if (generateDueType === 'quarter' && generateDueQuarter && generateDueYear) {
        // Generate for all 3 months in the quarter
        const quarterMonths = {
          'Q1': ['01', '02', '03'],
          'Q2': ['04', '05', '06'],
          'Q3': ['07', '08', '09'],
          'Q4': ['10', '11', '12']
        };
        // For now, use the first month of the quarter as target
        targetMonth = `${generateDueYear}-${quarterMonths[generateDueQuarter][0]}`;
      } else if (generateDueType === 'year' && generateDueYear) {
        // Generate for the first month of the year
        targetMonth = `${generateDueYear}-01`;
      } else {
        // Default to current month if nothing selected
        const today = new Date();
        targetMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
      }
      
      const response = await axios.post(`${API}/expenses/recurring/generate-due?target_month=${targetMonth}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setGenerateDueData(response.data);
      setSelectedDueExpenses(response.data.missing_expenses || []);
      setShowGenerateDueModal(true);
    } catch (error) {
      console.error('Error generating due expenses:', error);
      alert('Failed to generate due expenses: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleBulkCreatePending = async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (selectedDueExpenses.length === 0) {
        alert('No expenses selected');
        return;
      }
      
      // Ask for expense type
      const expenseTypeId = prompt(`Enter Expense Type ID (or select from: ${expenseTypes.map(t => t.name).join(', ')})`);
      if (!expenseTypeId) return;
      
      const today = new Date();
      const expensesData = selectedDueExpenses.map(exp => ({
        ...exp,
        amount: exp.last_amount || 0,
        date: `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`,
        payment_method: '',
        notes: 'Auto-created from recurring'
      }));
      
      await axios.post(`${API}/expenses/recurring/bulk-create`, 
        { expense_type_id: expenseTypeId, expenses_data: expensesData },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setShowGenerateDueModal(false);
      alert('Pending expenses created successfully!');
      fetchData();
    } catch (error) {
      console.error('Error bulk creating expenses:', error);
      alert('Failed to create pending expenses');
    }
  };

  const handleExport = async (format) => {
    try {
      const token = localStorage.getItem('token');
      const { start, end } = getDateRange();
      
      if (format === 'excel') {
        const response = await axios.get(`${API}/expenses/export/excel`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { start_date: start, end_date: end },
          responseType: 'blob'
        });
        
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `expenses_${start}_${end}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } else if (format === 'pdf') {
        const response = await axios.get(`${API}/expenses/export/pdf`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { start_date: start, end_date: end }
        });
        
        const newWindow = window.open('', '_blank');
        newWindow.document.write(response.data.html_content);
        newWindow.document.close();
        newWindow.print();
      }
    } catch (error) {
      console.error('Error exporting:', error);
      alert('Failed to export expenses');
    }
  };

  const resetExpenseForm = () => {
    setExpenseForm({
      expense_type_id: '',
      category_id: '',
      subcategory_id: '',
      amount: '',
      payment_method: 'Savings',
      transaction_number: '',
      date: new Date().toISOString().split('T')[0],
      notes: '',
      status: 'pending',
      recurring_type: null
    });
  };

  const selectedCategory = categories.find(c => c.id === expenseForm.category_id);
  const selectedSubcategory = selectedCategory?.subcategories?.find(s => s.id === expenseForm.subcategory_id);

  // Auto-fill recurring type and show last amount hint
  useEffect(() => {
    if (selectedSubcategory && !editingExpense) {
      setExpenseForm(prev => ({
        ...prev,
        recurring_type: selectedSubcategory.recurring_type || 'one_time'
      }));
    }
  }, [expenseForm.subcategory_id, selectedSubcategory, editingExpense]);

  if (loading && !dashboardStats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Expense Management</h1>
          <p className="text-gray-600">Track and manage all expenses with 3-level categorization</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowManageCategoriesModal(true)} variant="outline">
            <Edit className="w-4 h-4 mr-2" />
            Manage Categories
          </Button>
          <Button onClick={() => setShowAddExpenseModal(true)} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" />
            Add Expense
          </Button>
          <Button onClick={() => {
            // Initialize with current date
            const today = new Date();
            setGenerateDueMonth(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`);
            setGenerateDueYear(String(today.getFullYear()));
            setGenerateDueQuarter('Q1');
            setShowGenerateDueModal(true);
          }} variant="outline">
            <Clock className="w-4 h-4 mr-2" />
            Generate Due
          </Button>
        </div>
      </div>

      {/* Dashboard Stats */}
      {dashboardStats && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Expenses</p>
                    <p className="text-2xl font-bold">₹{dashboardStats.total_expenses.toLocaleString()}</p>
                  </div>
                  <IndianRupee className="w-8 h-8 text-blue-600" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Paid Amount</p>
                    <p className="text-2xl font-bold text-green-600">₹{dashboardStats.paid_expenses.toLocaleString()}</p>
                  </div>
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Pending Amount</p>
                    <p className="text-2xl font-bold text-orange-600">₹{dashboardStats.pending_expenses.toLocaleString()}</p>
                  </div>
                  <XCircle className="w-8 h-8 text-orange-600" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Pending Count</p>
                    <p className="text-2xl font-bold text-red-600">{dashboardStats.pending_payments_count || 0} items</p>
                  </div>
                  <AlertCircle className="w-8 h-8 text-red-600" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Alert for pending payments */}
          {dashboardStats.pending_payments_count > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-6 h-6 text-red-600" />
                <div>
                  <p className="font-semibold text-red-900">
                    ⚠️ {dashboardStats.pending_payments_count} pending payments (₹{dashboardStats.pending_expenses.toLocaleString()})
                  </p>
                  <p className="text-sm text-red-700">
                    Review and mark expenses as paid with transaction numbers
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Expense by Type Pie Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Expenses by Type</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-center">
                {(() => {
                  // Calculate expenses by type
                  const typeData = expenseTypes.map(type => {
                    const typeExpenses = expenses.filter(exp => exp.expense_type_id === type.id);
                    const total = typeExpenses.reduce((sum, exp) => sum + (exp.amount || 0), 0);
                    return {
                      name: type.name,
                      value: total,
                      count: typeExpenses.length
                    };
                  }).filter(item => item.value > 0);
                  
                  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];
                  
                  if (typeData.length === 0) {
                    return (
                      <div className="text-center text-gray-500 py-8">
                        <p>No expense data available</p>
                      </div>
                    );
                  }
                  
                  const total = typeData.reduce((sum, item) => sum + item.value, 0);
                  
                  return (
                    <div className="flex items-center gap-8">
                      <ResponsiveContainer width={250} height={250}>
                        <PieChart>
                          <Pie
                            data={typeData}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={100}
                            paddingAngle={2}
                            dataKey="value"
                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          >
                            {typeData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip 
                            formatter={(value) => `₹${value.toLocaleString()}`}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                      
                      {/* Legend with details */}
                      <div className="space-y-3">
                        {typeData.map((item, index) => (
                          <div key={item.name} className="flex items-center gap-3">
                            <div 
                              className="w-4 h-4 rounded-full" 
                              style={{ backgroundColor: COLORS[index % COLORS.length] }}
                            />
                            <div>
                              <p className="font-semibold">{item.name}</p>
                              <p className="text-sm text-gray-600">
                                ₹{item.value.toLocaleString()} ({item.count} expenses)
                              </p>
                              <p className="text-xs text-gray-400">
                                {((item.value / total) * 100).toFixed(1)}% of total
                              </p>
                            </div>
                          </div>
                        ))}
                        <div className="border-t pt-2 mt-2">
                          <p className="font-bold text-lg">Total: ₹{total.toLocaleString()}</p>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-7 gap-4">
            <div>
              <Label>Date Range</Label>
              <select 
                className="w-full p-2 border rounded"
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
              >
                <option value="today">Today</option>
                <option value="this_week">This Week</option>
                <option value="this_month">This Month</option>
                <option value="custom">Custom</option>
              </select>
            </div>
            
            {dateRange === 'custom' && (
              <>
                <div>
                  <Label>Start Date</Label>
                  <input 
                    type="date" 
                    className="w-full p-2 border rounded"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div>
                  <Label>End Date</Label>
                  <input 
                    type="date" 
                    className="w-full p-2 border rounded"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
              </>
            )}
            
            <div>
              <Label>Expense Type</Label>
              <select 
                className="w-full p-2 border rounded"
                value={filterExpenseType}
                onChange={(e) => setFilterExpenseType(e.target.value)}
              >
                <option value="">All</option>
                {expenseTypes.map(type => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </select>
            </div>
            
            <div>
              <Label>Category</Label>
              <select 
                className="w-full p-2 border rounded"
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
              >
                <option value="">All</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
            
            <div>
              <Label>Payment Method</Label>
              <select 
                className="w-full p-2 border rounded"
                value={filterPaymentMethod}
                onChange={(e) => setFilterPaymentMethod(e.target.value)}
              >
                <option value="">All</option>
                {paymentMethods.map(pm => (
                  <option key={pm} value={pm}>{pm}</option>
                ))}
              </select>
            </div>
            
            <div>
              <Label>Recurring Type</Label>
              <select 
                className="w-full p-2 border rounded"
                value={filterRecurringType}
                onChange={(e) => setFilterRecurringType(e.target.value)}
              >
                <option value="">All</option>
                <option value="one_time">One Time</option>
                <option value="fixed_recurring">Fixed Recurring</option>
                <option value="variable_recurring">Variable Recurring</option>
              </select>
            </div>
            
            <div>
              <Label>Status</Label>
              <select 
                className="w-full p-2 border rounded"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="">All</option>
                <option value="paid">Paid</option>
                <option value="pending">Pending</option>
              </select>
            </div>
            
            <div>
              <Label>Search</Label>
              <input 
                type="text"
                placeholder="Search..."
                className="w-full p-2 border rounded"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
          
          <div className="mt-4 flex gap-2">
            <Button onClick={() => handleExport('excel')} variant="outline" size="sm">
              <Download className="w-4 h-4 mr-2" />
              Export Excel
            </Button>
            <Button onClick={() => handleExport('pdf')} variant="outline" size="sm">
              <Download className="w-4 h-4 mr-2" />
              Export PDF
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Charts */}
      {dashboardStats && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Category Breakdown Pie Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Expense by Category</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={Object.entries(dashboardStats.category_breakdown).map(([name, value]) => ({ name, value }))}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {Object.entries(dashboardStats.category_breakdown).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Payment Method Pie Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Expense by Payment Method</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={Object.entries(dashboardStats.payment_breakdown).map(([name, value]) => ({ name, value }))}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {Object.entries(dashboardStats.payment_breakdown).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Monthly Trend Line Chart */}
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle>Monthly Spending Trend</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={dashboardStats.monthly_trend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="amount" stroke="#3b82f6" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Recurring Type Bar Chart */}
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle>Recurring Type Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={[
                  { name: 'One Time', amount: dashboardStats.type_breakdown.one_time || 0 },
                  { name: 'Fixed Recurring', amount: dashboardStats.type_breakdown.fixed_recurring || 0 },
                  { name: 'Variable Recurring', amount: dashboardStats.type_breakdown.variable_recurring || 0 }
                ]}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="amount" fill="#10b981" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Expenses Table */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Expenses</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">Date</th>
                  <th className="text-left p-2">Expense Type</th>
                  <th className="text-left p-2">Category</th>
                  <th className="text-left p-2">Subcategory</th>
                  <th className="text-right p-2">Amount</th>
                  <th className="text-left p-2">Payment</th>
                  <th className="text-left p-2">Type</th>
                  <th className="text-left p-2">Status</th>
                  <th className="text-left p-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {expenses.map(expense => (
                  <tr key={expense.id} className="border-b hover:bg-gray-50">
                    <td className="p-2">{expense.date}</td>
                    <td className="p-2">{expense.expense_type_name}</td>
                    <td className="p-2">{expense.category_name}</td>
                    <td className="p-2">{expense.subcategory_name || '-'}</td>
                    <td className="p-2 text-right font-semibold">₹{expense.amount.toLocaleString()}</td>
                    <td className="p-2">{expense.payment_method}</td>
                    <td className="p-2">
                      <span className={`px-2 py-1 rounded text-xs ${
                        expense.recurring_type === 'fixed_recurring' 
                          ? 'bg-purple-100 text-purple-800' 
                          : expense.recurring_type === 'variable_recurring'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {expense.recurring_type === 'fixed_recurring' ? 'Fixed' :
                         expense.recurring_type === 'variable_recurring' ? 'Variable' : 'One Time'}
                      </span>
                    </td>
                    <td className="p-2">
                      <span className={`px-2 py-1 rounded text-xs ${
                        expense.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'
                      }`}>
                        {expense.status}
                      </span>
                    </td>
                    <td className="p-2">
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setEditingExpense(expense);
                            setExpenseForm({
                              expense_type_id: expense.expense_type_id,
                              category_id: expense.category_id,
                              subcategory_id: expense.subcategory_id || '',
                              amount: expense.amount.toString(),
                              payment_method: expense.payment_method,
                              transaction_number: expense.transaction_number || '',
                              date: expense.date,
                              notes: expense.notes || '',
                              status: expense.status,
                              recurring_type: expense.recurring_type
                            });
                            setShowAddExpenseModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteExpense(expense.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Expense Modal */}
      <Dialog open={showAddExpenseModal} onOpenChange={setShowAddExpenseModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingExpense ? 'Edit' : 'Add'} Expense</DialogTitle>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-4">
            {/* Level 1: Expense Type */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Label>Expense Type (Level 1) *</Label>
                <button
                  onClick={() => setShowAddTypeModal(true)}
                  className="text-blue-600 hover:text-blue-800"
                  title="Add new expense type"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <select
                className="w-full p-2 border rounded"
                value={expenseForm.expense_type_id}
                onChange={(e) => setExpenseForm({ ...expenseForm, expense_type_id: e.target.value })}
              >
                <option value="">Select Expense Type</option>
                {expenseTypes.map(type => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </select>
            </div>

            {/* Level 2: Category */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Label>Category (Level 2) *</Label>
                <button
                  onClick={() => setShowAddCategoryModal(true)}
                  className="text-blue-600 hover:text-blue-800"
                  title="Add new category"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <select
                className="w-full p-2 border rounded"
                value={expenseForm.category_id}
                onChange={(e) => setExpenseForm({ ...expenseForm, category_id: e.target.value, subcategory_id: '' })}
              >
                <option value="">Select Category</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>

            {/* Level 3: Subcategory */}
            <div className="col-span-2">
              <div className="flex items-center gap-2 mb-2">
                <Label>Subcategory (Level 3)</Label>
                <button
                  onClick={() => {
                    setNewSubcategoryForm({ ...newSubcategoryForm, category_id: expenseForm.category_id });
                    setShowAddSubcategoryModal(true);
                  }}
                  className="text-blue-600 hover:text-blue-800"
                  title="Add new subcategory"
                  disabled={!expenseForm.category_id}
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <select
                className="w-full p-2 border rounded"
                value={expenseForm.subcategory_id}
                onChange={(e) => setExpenseForm({ ...expenseForm, subcategory_id: e.target.value })}
                disabled={!selectedCategory || selectedCategory.subcategories.length === 0}
              >
                <option value="">Select Subcategory</option>
                {selectedCategory && selectedCategory.subcategories.map(sub => (
                  <option key={sub.id} value={sub.id}>
                    {sub.name} 
                    {sub.recurring_type && ` (${sub.recurring_type === 'fixed_recurring' ? 'Fixed' : sub.recurring_type === 'variable_recurring' ? 'Variable' : 'One Time'})`}
                  </option>
                ))}
              </select>
              {/* Edit/Delete buttons for selected subcategory */}
              {expenseForm.subcategory_id && selectedSubcategory && (
                <div className="flex gap-2 mt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setEditingSubcategory({
                        id: selectedSubcategory.id,
                        category_id: expenseForm.category_id,
                        name: selectedSubcategory.name,
                        recurring_type: selectedSubcategory.recurring_type || 'one_time'
                      });
                      setShowEditSubcategoryModal(true);
                    }}
                    className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                  >
                    <Edit className="w-3 h-3" /> Edit Subcategory
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteSubcategory(expenseForm.category_id, selectedSubcategory.id, selectedSubcategory.name)}
                    className="text-xs text-red-600 hover:text-red-800 flex items-center gap-1"
                  >
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </div>
              )}
              {selectedSubcategory && selectedSubcategory.last_amount && (
                <p className="text-sm text-gray-600 mt-1">
                  💡 Last amount: ₹{selectedSubcategory.last_amount.toLocaleString()} 
                  {selectedSubcategory.last_paid_date && ` (${selectedSubcategory.last_paid_date})`}
                </p>
              )}
            </div>

            <div>
              <Label>Amount (₹) *</Label>
              <input
                type="number"
                className="w-full p-2 border rounded"
                value={expenseForm.amount}
                onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                placeholder="0.00"
              />
            </div>
            
            <div>
              <Label>Payment Method *</Label>
              <select
                className="w-full p-2 border rounded"
                value={expenseForm.payment_method}
                onChange={(e) => setExpenseForm({ ...expenseForm, payment_method: e.target.value })}
              >
                {paymentMethods.map(pm => (
                  <option key={pm} value={pm}>{pm}</option>
                ))}
              </select>
            </div>
            
            <div>
              <Label>Transaction Number</Label>
              <input
                type="text"
                className="w-full p-2 border rounded"
                value={expenseForm.transaction_number}
                onChange={(e) => setExpenseForm({ ...expenseForm, transaction_number: e.target.value })}
                placeholder="Optional (or 'Pending')"
              />
            </div>
            
            <div>
              <Label>Date *</Label>
              <input
                type="date"
                className="w-full p-2 border rounded"
                value={expenseForm.date}
                onChange={(e) => setExpenseForm({ ...expenseForm, date: e.target.value })}
              />
            </div>
            
            <div>
              <Label>Status *</Label>
              <select
                className="w-full p-2 border rounded"
                value={expenseForm.status}
                onChange={(e) => setExpenseForm({ ...expenseForm, status: e.target.value })}
              >
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
              </select>
            </div>
            
            <div>
              <Label>Recurring Type (Override)</Label>
              <select
                className="w-full p-2 border rounded"
                value={expenseForm.recurring_type || ''}
                onChange={(e) => setExpenseForm({ ...expenseForm, recurring_type: e.target.value || null })}
              >
                <option value="">Auto (from subcategory)</option>
                <option value="one_time">One Time</option>
                <option value="fixed_recurring">Fixed Recurring</option>
                <option value="variable_recurring">Variable Recurring</option>
              </select>
            </div>
            
            <div className="col-span-2">
              <Label>Notes</Label>
              <Textarea
                className="w-full"
                value={expenseForm.notes}
                onChange={(e) => setExpenseForm({ ...expenseForm, notes: e.target.value })}
                placeholder="Additional notes..."
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowAddExpenseModal(false);
              setEditingExpense(null);
              resetExpenseForm();
            }}>
              Cancel
            </Button>
            <Button onClick={handleAddExpense}>
              {editingExpense ? 'Update' : 'Add'} Expense
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Expense Type Modal */}
      <Dialog open={showAddTypeModal} onOpenChange={setShowAddTypeModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Expense Type</DialogTitle>
          </DialogHeader>
          <div>
            <Label>Type Name *</Label>
            <input
              type="text"
              className="w-full p-2 border rounded"
              value={newTypeName}
              onChange={(e) => setNewTypeName(e.target.value)}
              placeholder="e.g., Business, Family, etc."
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowAddTypeModal(false);
              setNewTypeName('');
            }}>
              Cancel
            </Button>
            <Button onClick={handleAddExpenseType}>Add Type</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Category Modal */}
      <Dialog open={showAddCategoryModal} onOpenChange={setShowAddCategoryModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Category</DialogTitle>
          </DialogHeader>
          <div>
            <Label>Category Name *</Label>
            <input
              type="text"
              className="w-full p-2 border rounded"
              value={newCategoryForm.name}
              onChange={(e) => setNewCategoryForm({ ...newCategoryForm, name: e.target.value })}
              placeholder="e.g., Marketing, Travel, etc."
            />
            <p className="text-sm text-gray-600 mt-2">Note: You can add subcategories later using the + button</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowAddCategoryModal(false);
              setNewCategoryForm({ name: '', subcategories: [] });
            }}>
              Cancel
            </Button>
            <Button onClick={handleAddCategory}>Add Category</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Subcategory Modal */}
      <Dialog open={showAddSubcategoryModal} onOpenChange={setShowAddSubcategoryModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Subcategory</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Show selected category */}
            <div className="bg-blue-50 p-3 rounded">
              <p className="text-sm text-blue-800">
                Adding subcategory to: <strong>{categories.find(c => c.id === newSubcategoryForm.category_id)?.name || 'Unknown Category'}</strong>
              </p>
            </div>
            
            <div>
              <Label>Subcategory Name *</Label>
              <input
                type="text"
                className="w-full p-2 border rounded"
                value={newSubcategoryForm.name}
                onChange={(e) => setNewSubcategoryForm({ ...newSubcategoryForm, name: e.target.value })}
                placeholder="e.g., John Doe, Office Block A, etc."
              />
            </div>
            <div>
              <Label>Recurring Type *</Label>
              <select
                className="w-full p-2 border rounded"
                value={newSubcategoryForm.recurring_type}
                onChange={(e) => setNewSubcategoryForm({ ...newSubcategoryForm, recurring_type: e.target.value })}
              >
                <option value="one_time">One Time</option>
                <option value="fixed_recurring">Fixed Recurring (same amount)</option>
                <option value="variable_recurring">Variable Recurring (amount varies)</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowAddSubcategoryModal(false);
              setNewSubcategoryForm({ category_id: '', name: '', recurring_type: 'one_time' });
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddSubcategory}
              disabled={!newSubcategoryForm.name || !newSubcategoryForm.category_id}
            >
              Add Subcategory
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Subcategory Modal */}
      <Dialog open={showEditSubcategoryModal} onOpenChange={setShowEditSubcategoryModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Subcategory</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Subcategory Name *</Label>
              <input
                type="text"
                className="w-full p-2 border rounded"
                value={editingSubcategory?.name || ''}
                onChange={(e) => setEditingSubcategory({ ...editingSubcategory, name: e.target.value })}
                placeholder="e.g., John Doe, Office Block A, etc."
              />
            </div>
            <div>
              <Label>Recurring Type *</Label>
              <select
                className="w-full p-2 border rounded"
                value={editingSubcategory?.recurring_type || 'one_time'}
                onChange={(e) => setEditingSubcategory({ ...editingSubcategory, recurring_type: e.target.value })}
              >
                <option value="one_time">One Time</option>
                <option value="fixed_recurring">Fixed Recurring (same amount)</option>
                <option value="variable_recurring">Variable Recurring (amount varies)</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowEditSubcategoryModal(false);
              setEditingSubcategory(null);
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleEditSubcategory}
              disabled={!editingSubcategory?.name}
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Category Modal */}
      <Dialog open={showEditCategoryModal} onOpenChange={setShowEditCategoryModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Category</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Category Name *</Label>
              <input
                type="text"
                className="w-full p-2 border rounded"
                value={editingCategory?.name || ''}
                onChange={(e) => setEditingCategory({ ...editingCategory, name: e.target.value })}
                placeholder="e.g., Rent, Utilities, etc."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowEditCategoryModal(false);
              setEditingCategory(null);
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleEditCategory}
              disabled={!editingCategory?.name}
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage Categories Modal */}
      <Dialog open={showManageCategoriesModal} onOpenChange={setShowManageCategoriesModal}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Manage Categories & Subcategories</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Add New Category Button */}
            <div className="flex justify-end">
              <Button onClick={() => {
                setShowManageCategoriesModal(false);
                setShowAddCategoryModal(true);
              }} size="sm">
                <Plus className="w-4 h-4 mr-1" /> Add Category
              </Button>
            </div>
            
            {/* Categories List */}
            <div className="space-y-4">
              {categories.map(category => (
                <Card key={category.id} className="border">
                  <CardHeader className="py-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">{category.name}</CardTitle>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEditingCategory({ id: category.id, name: category.name });
                            setShowEditCategoryModal(true);
                          }}
                        >
                          <Edit className="w-4 h-4 mr-1" /> Edit
                        </Button>
                        {!category.is_default && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleDeleteCategory(category.id, category.name)}
                          >
                            <Trash2 className="w-4 h-4 mr-1" /> Delete
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="py-2">
                    {/* Subcategories */}
                    <div className="pl-4 border-l-2 border-gray-200">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-600">Subcategories:</span>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-blue-600 h-7"
                          onClick={() => {
                            setNewSubcategoryForm({ category_id: category.id, name: '', recurring_type: 'one_time' });
                            setShowManageCategoriesModal(false);
                            setShowAddSubcategoryModal(true);
                          }}
                        >
                          <Plus className="w-3 h-3 mr-1" /> Add
                        </Button>
                      </div>
                      {category.subcategories && category.subcategories.length > 0 ? (
                        <div className="space-y-2">
                          {category.subcategories.map(sub => (
                            <div key={sub.id} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                              <div>
                                <span className="font-medium">{sub.name}</span>
                                <span className="text-xs text-gray-500 ml-2">
                                  ({sub.recurring_type === 'fixed_recurring' ? 'Fixed' : sub.recurring_type === 'variable_recurring' ? 'Variable' : 'One Time'})
                                </span>
                              </div>
                              <div className="flex gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 text-blue-600"
                                  onClick={() => {
                                    setEditingSubcategory({
                                      id: sub.id,
                                      category_id: category.id,
                                      name: sub.name,
                                      recurring_type: sub.recurring_type || 'one_time'
                                    });
                                    setShowManageCategoriesModal(false);
                                    setShowEditSubcategoryModal(true);
                                  }}
                                >
                                  <Edit className="w-3 h-3" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 text-red-600"
                                  onClick={() => handleDeleteSubcategory(category.id, sub.id, sub.name)}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-400 italic">No subcategories</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowManageCategoriesModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generate Due Modal */}
      <Dialog open={showGenerateDueModal} onOpenChange={setShowGenerateDueModal}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Generate Due Recurring Expenses</DialogTitle>
          </DialogHeader>
          
          {!generateDueData ? (
            /* Selection Form */
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded">
                <p className="text-sm text-blue-800 mb-2">Select the time period for generating recurring expenses:</p>
              </div>
              
              {/* Period Type Selection */}
              <div>
                <Label>Period Type *</Label>
                <select
                  className="w-full p-2 border rounded"
                  value={generateDueType}
                  onChange={(e) => setGenerateDueType(e.target.value)}
                >
                  <option value="month">Specific Month</option>
                  <option value="quarter">Quarter</option>
                  <option value="year">Year</option>
                </select>
              </div>

              {/* Month Selection */}
              {generateDueType === 'month' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Select Month *</Label>
                    <select
                      className="w-full p-2 border rounded"
                      value={generateDueMonth.split('-')[1] || '01'}
                      onChange={(e) => {
                        const year = generateDueMonth.split('-')[0] || new Date().getFullYear();
                        setGenerateDueMonth(`${year}-${e.target.value}`);
                      }}
                    >
                      <option value="01">January</option>
                      <option value="02">February</option>
                      <option value="03">March</option>
                      <option value="04">April</option>
                      <option value="05">May</option>
                      <option value="06">June</option>
                      <option value="07">July</option>
                      <option value="08">August</option>
                      <option value="09">September</option>
                      <option value="10">October</option>
                      <option value="11">November</option>
                      <option value="12">December</option>
                    </select>
                  </div>
                  <div>
                    <Label>Select Year *</Label>
                    <input
                      type="number"
                      className="w-full p-2 border rounded"
                      value={generateDueMonth.split('-')[0] || new Date().getFullYear()}
                      onChange={(e) => {
                        const month = generateDueMonth.split('-')[1] || '01';
                        setGenerateDueMonth(`${e.target.value}-${month}`);
                      }}
                      min="2020"
                      max="2050"
                    />
                  </div>
                </div>
              )}

              {/* Quarter Selection */}
              {generateDueType === 'quarter' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Select Quarter *</Label>
                    <select
                      className="w-full p-2 border rounded"
                      value={generateDueQuarter}
                      onChange={(e) => setGenerateDueQuarter(e.target.value)}
                    >
                      <option value="Q1">Q1 (Jan-Mar)</option>
                      <option value="Q2">Q2 (Apr-Jun)</option>
                      <option value="Q3">Q3 (Jul-Sep)</option>
                      <option value="Q4">Q4 (Oct-Dec)</option>
                    </select>
                  </div>
                  <div>
                    <Label>Select Year *</Label>
                    <input
                      type="number"
                      className="w-full p-2 border rounded"
                      value={generateDueYear}
                      onChange={(e) => setGenerateDueYear(e.target.value)}
                      min="2020"
                      max="2050"
                    />
                  </div>
                </div>
              )}

              {/* Year Selection */}
              {generateDueType === 'year' && (
                <div>
                  <Label>Select Year *</Label>
                  <input
                    type="number"
                    className="w-full p-2 border rounded"
                    value={generateDueYear}
                    onChange={(e) => setGenerateDueYear(e.target.value)}
                    min="2020"
                    max="2050"
                  />
                </div>
              )}
            </div>
          ) : (
            /* Results Display */
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded">
                <p className="font-semibold">Target Month: {generateDueData.target_month}</p>
                <p>Missing Recurring Expenses: {generateDueData.missing_count}</p>
                <p>Estimated Total: ₹{generateDueData.estimated_total?.toLocaleString()}</p>
              </div>

              {generateDueData.missing_expenses.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-2">Category</th>
                        <th className="text-left p-2">Subcategory</th>
                        <th className="text-left p-2">Type</th>
                        <th className="text-right p-2">Last Amount</th>
                        <th className="text-left p-2">Last Paid</th>
                      </tr>
                    </thead>
                    <tbody>
                      {generateDueData.missing_expenses.map((exp, idx) => (
                        <tr key={idx} className="border-b hover:bg-gray-50">
                          <td className="p-2">{exp.category_name}</td>
                          <td className="p-2">{exp.subcategory_name}</td>
                          <td className="p-2">
                            <span className={`px-2 py-1 rounded text-xs ${
                              exp.recurring_type === 'fixed_recurring' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                            }`}>
                              {exp.recurring_type === 'fixed_recurring' ? 'Fixed' : 'Variable'}
                            </span>
                          </td>
                          <td className="p-2 text-right">
                            {exp.last_amount ? `₹${exp.last_amount.toLocaleString()}` : '-'}
                          </td>
                          <td className="p-2">{exp.last_paid_date || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-center text-gray-600 py-4">All recurring expenses are up to date!</p>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowGenerateDueModal(false);
              setGenerateDueData(null);
            }}>
              {generateDueData ? 'Close' : 'Cancel'}
            </Button>
            {!generateDueData && (
              <Button onClick={handleGenerateDue}>
                Check Due Expenses
              </Button>
            )}
            {generateDueData && generateDueData.missing_count > 0 && (
              <Button onClick={handleBulkCreatePending}>
                Create All as Pending
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExpenseManagement;
